import client from 'core/axios';
import handleError from 'core/axios/handleError';
import { handleJSONResponse } from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';

/**
 * Get list of breakdown options for quiz completion
 *
 * @param {string} version
 */
export const getBreakdownOptionsForQuizCompletion = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/elementary/quizzes/completion/options/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Retrieve a list of careers
 *
 * @param {Object} params - request query parameters
 * @param {number} params.liked - used to get liked careers
 * @param {number} params.group - to filter by group id
 * @param {quizAnswers} params.quizAnswers - array of quiz answers
 * @param {string} version
 */
export const getCareerList = ({ params = {}, version = 'v1' }) =>
  client
    .request({
      url: `${version}/elementary/careers/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get quiz completion detail by breakdown type
 *
 * @param {string} breakdownType - breakdown type
 * @param {string} version
 */
export const getQuizCompletionDetailsByBreakdownType = ({
  breakdownType,
  params,
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/elementary/quizzes/completion/options/${breakdownType}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Retrieve a list of clusters
 * @param {string} version
 */
export const getClusterList = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/elementary/career-groups/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get liked careers
 *
 * @param {string} params.schools - breakdown type schools
 * @param {string} params.grades - breakdown type grades
 * @param {string} params.sections - breakdown type sections
 *
 * @param {string} version
 */
export const getLikedCareers = ({ params, version = 'v1' }) =>
  client
    .request({
      url: `${version}/elementary/careers/liked/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Retrieve career details
 * @param {number} careerId
 * @param {string} version
 */
export const getCareerDetails = ({ careerId, version = 'v1' }) =>
  client
    .request({
      url: `${version}/elementary/careers/${careerId}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get students by breakdown type
 *
 * @param {string} breakdownType - breakdown type
 * @param {string} version
 */
export const getStudentsByBreakdownType = ({
  breakdownType,
  params,
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/sl_users/students/${breakdownType}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Retrieve career details
 * @param {number} clusterId
 * @param {string} version
 */
export const getClusterDetails = ({ clusterId, version = 'v1' }) =>
  client
    .request({
      url: `${version}/elementary/career-groups/${clusterId}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Like a career
 * @param {number} careerId
 * @param {string} version
 */
export const likeCareer = ({ careerId, version = 'v1' }) =>
  client
    .request({
      url: `${version}/elementary/careers/${careerId}/like/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Dislike a career
 * @param {number} careerId
 * @param {string} version
 */
export const dislikeCareer = ({ careerId, version = 'v1' }) =>
  client
    .request({
      url: `${version}/elementary/careers/${careerId}/dislike/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Create a Quiz
 * @param {string} version
 */
export const createQuiz = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/elementary/quizzes/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get All Quizzes
 * @param {string} version
 */
export const getQuizList = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/elementary/quizzes/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get Question
 * @param {string} version
 */
export const getQuestion = ({ quizId, version = 'v1' }) =>
  client
    .request({
      url: `${version}/elementary/quizzes/${quizId}/question/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Add answer
 * @param {string} version
 */
export const addAnswer = ({ quizId, data, version = 'v1' }) =>
  client
    .request({
      url: `${version}/elementary/quizzes/${quizId}/add-answer/`,
      method: RequestMethod.POST,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Remove answer
 * @param {string} version
 */
export const removeAnswer = ({ quizId, answerId, version = 'v1' }) =>
  client
    .request({
      url: `${version}/elementary/quizzes/${quizId}/remove-answer/${answerId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get audio urls for texts
 *
 * @param {Array} data
 */
export const getTextToSpeech = ({ data, params, version = 'v1' }) =>
  client
    .request({
      url: `${version}/common/text-to-speech/`,
      method: RequestMethod.POST,
      data,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get check-in list
 *
 * @return 404 error code if there are no checkins for the current user
 */
export const getCheckin = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/checkins/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get survey question list
 *
 * @param {String} checkInSlug
 */
export const getSurveyQuestionList = ({ checkInSlug, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/surveys/${checkInSlug}/questions/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Add survey answer
 *
 * @param {String} checkInSlug
 * @param {Object} data
 */
export const submitSurveyAnswers = ({
  surveyInstanceId,
  data,
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/surveys/instances/${surveyInstanceId}/answers-pk/`,
      method: RequestMethod.POST,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get survey list
 *
 * @param {String} version
 * @param {Object} data
 */
export const getSurveyList = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/surveys/user-surveys/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get elementary checkin stats
 *
 * @param {String} version
 * @param {Number} userId
 * @param {Object} params
 */
export const getElementaryCheckInStats = ({ userId, params, version = 'v1' }) =>
  client
    .request({
      url: `${version}/checkins/stats/${userId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get elementary checkin answers
 *
 * @param {String} version
 * @param {Object} params
 * @param {Number} params.user - user id
 * @param {Number} params.category - category
 * @param {String} params.completed_date_after - completed date after
 * @param {String} params.completed_date_before - completed date before
 */
export const getElementaryCheckInAnswers = ({ params, version = 'v1' }) =>
  client
    .request({
      url: `${version}/checkins/answers/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
